/**
 * Zeitstrahl (Hans 06.10.2026): jede Meldung als Balken auf einer Zeitachse
 * mit Uhrzeit — Start ist die Meldung, die Schritte der Status-Kette stehen
 * als Punkte auf dem Balken.
 *
 *  - Endet die Arbeit (erledigt, abgelehnt, verworfen, zusammengeführt),
 *    endet der Balken zum Zeitpunkt dieses Schritts.
 *  - Alle anderen laufen bis JETZT und tragen den aktuellen Status mit
 *    „wartet auf …" am Ende.
 *
 * Tagesauswahl: Ist ein Tag gewählt, bleiben nur Meldungen sichtbar, die an
 * diesem Tag angefangen, bearbeitet (Nachricht, Statuswechsel, Kettenschritt)
 * oder beendet wurden. Klick auf die Zeitachse wählt den Tag.
 *
 * Später (Konzept): Ende „Live-Deploy" aus den Deploy-Läufen der Anwendung.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { statusMitZug } from "@/lib/labels"
import { schrittArt, type SchrittArt } from "@/components/meldung/StatusKette"
import { Kalender } from "@/components/meldung/Kalender"
import type { Report, ReportStatus } from "@/types"

const STUNDE = 3_600_000

/** Status, mit denen die Arbeit an einer Meldung endet */
const ENDE_STATI: ReportStatus[] = ["erledigt", "abgelehnt", "verworfen"]

type Zoom = "tag" | "woche" | "monat"
const ZOOM: Record<Zoom, { label: string; pxProStunde: number; fenster: number }> = {
  tag: { label: "24 Std", pxProStunde: 48, fenster: 24 },
  woche: { label: "Woche", pxProStunde: 8, fenster: 24 * 7 },
  monat: { label: "Monat", pxProStunde: 2, fenster: 24 * 30 },
}

const PUNKT: Record<SchrittArt, string> = {
  neutral: "bg-slate-200",
  rueckfrage: "bg-warning",
  bereit: "bg-sky-400",
  freigabe: "bg-success",
  ende: "bg-destructive",
}

const BALKEN: Record<"offen" | "erledigt" | "beendet", string> = {
  offen: "bg-primary/55 border-primary",
  erledigt: "bg-success/45 border-success",
  beendet: "bg-destructive/35 border-destructive",
}

const LABEL_BREITE = 280 // linke Spalte: Issue + Titel
const RAUM_RECHTS = 300 // Platz rechts von „jetzt" für die Status-Beschriftung
const ZEILE = 34

const _zeit = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
})
const _tag = new Intl.DateTimeFormat("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })
const _tagKurz = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit" })
const _tagLang = new Intl.DateTimeFormat("de-DE", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
})

/** Lokaler Kalendertag als YYYY-MM-DD */
export function tagSchluessel(t: number | Date): string {
  const d = new Date(t)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

function tagBeginnMs(tag: string): number {
  const [j, m, t] = tag.split("-").map(Number)
  return new Date(j, m - 1, t).getTime()
}

function tagVerschieben(tag: string, tage: number): string {
  const d = new Date(tagBeginnMs(tag))
  d.setDate(d.getDate() + tage)
  return tagSchluessel(d)
}

/** Hat die Meldung an diesem Tag angefangen, wurde bearbeitet oder beendet? */
function aktivAm(b: Balken, tag: string): boolean {
  return (
    tagSchluessel(b.start) === tag ||
    (!b.offen && tagSchluessel(b.ende) === tag) ||
    b.punkte.some((p) => tagSchluessel(p.zeit) === tag) ||
    (b.report.aktivitaetTage ?? []).includes(tag)
  )
}

interface Balken {
  report: Report
  start: number
  ende: number
  offen: boolean
  art: "offen" | "erledigt" | "beendet"
  punkte: { zeit: number; label: string; art: SchrittArt }[]
}

function balkenBauen(reports: Report[], jetzt: number): Balken[] {
  return reports
    .filter((r) => r.status !== "entwurf")
    .map((r) => {
      const kette = (r.kette ?? []).filter((s) => s.zeit)
      const start = kette.length > 0 ? new Date(kette[0].zeit!).getTime() : new Date(r.createdAt).getTime()
      const beendet = ENDE_STATI.includes(r.status)
      const letzter = kette[kette.length - 1]
      const ende = beendet && letzter ? new Date(letzter.zeit!).getTime() : jetzt
      return {
        report: r,
        start,
        ende: Math.max(ende, start),
        offen: !beendet,
        art: !beendet ? "offen" : r.status === "erledigt" ? "erledigt" : "beendet",
        // Punkte nur innerhalb des Balkens (Uhrzeitversatz o. Ä. nie hinter „jetzt")
        punkte: kette
          .slice(1)
          .map((s) => ({
            zeit: new Date(s.zeit!).getTime(),
            label: s.label,
            art: schrittArt(s.schluessel),
          }))
          .filter((p) => p.zeit <= Math.max(ende, start)),
      } satisfies Balken
    })
    .sort((a, b) => a.start - b.start)
}

/** Achsenteilung je Zoom: kleine Striche, beschriftete Hauptstriche */
function achse(von: number, bis: number, zoom: Zoom) {
  const schritt = zoom === "tag" ? 1 : zoom === "woche" ? 6 : 24 // Stunden
  const erster = new Date(von)
  if (zoom === "tag") erster.setMinutes(0, 0, 0)
  else erster.setHours(0, 0, 0, 0) // Woche/Monat: Striche ab Mitternacht
  const striche: { zeit: number; haupt: boolean; text: string }[] = []
  for (let t = erster.getTime(); t <= bis; t += schritt * STUNDE) {
    const d = new Date(t)
    const tageswechsel = d.getHours() === 0
    const haupt = zoom === "tag" ? true : tageswechsel
    const text =
      zoom === "tag"
        ? tageswechsel
          ? _tagKurz.format(d)
          : `${String(d.getHours()).padStart(2, "0")}:00`
        : tageswechsel
          ? zoom === "monat"
            ? _tagKurz.format(d)
            : _tag.format(d)
          : ""
    striche.push({ zeit: t, haupt, text })
  }
  return striche
}

export function Zeitstrahl({
  reports,
  tag,
  onTag,
}: {
  reports: Report[]
  /** gewählter Tag (YYYY-MM-DD) oder null = alle */
  tag: string | null
  onTag: (tag: string | null) => void
}) {
  const navigate = useNavigate()
  const [zoom, setZoom] = useState<Zoom>("woche")
  // „jetzt" läuft mit — offene Balken wachsen sichtbar
  const [jetzt, setJetzt] = useState(() => Date.now())
  useEffect(() => {
    const t = window.setInterval(() => setJetzt(Date.now()), 60_000)
    return () => window.clearInterval(t)
  }, [])

  const alleBalken = useMemo(() => balkenBauen(reports, jetzt), [reports, jetzt])
  const balken = useMemo(
    () => (tag ? alleBalken.filter((b) => aktivAm(b, tag)) : alleBalken),
    [alleBalken, tag]
  )
  const { pxProStunde, fenster } = ZOOM[zoom]
  // Achse über ALLE Balken — bleibt beim Tageswechsel stehen
  const fruehester = alleBalken.length > 0 ? Math.min(...alleBalken.map((b) => b.start)) : jetzt
  // Mindestens ein volles Fenster zeigen, auch wenn alles jünger ist
  const von = Math.min(fruehester - 2 * STUNDE, jetzt - fenster * STUNDE)
  const x = (t: number) => ((t - von) / STUNDE) * pxProStunde
  const breite = x(jetzt) + RAUM_RECHTS
  const striche = useMemo(() => achse(von, jetzt, zoom), [von, jetzt, zoom])

  // Beim Öffnen und Zoomen ans rechte Ende („jetzt") scrollen — ist ein Tag
  // gewählt, diesen Tag in die Mitte holen
  const scrollRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (tag) {
      const mitte = x(tagBeginnMs(tag) + 12 * STUNDE)
      el.scrollLeft = Math.max(0, mitte - el.clientWidth / 2)
    } else {
      el.scrollLeft = el.scrollWidth
    }
    // x hängt an von/zoom — beides steckt in den Abhängigkeiten
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, tag, von, balken.length])

  const heute = tagSchluessel(jetzt)
  // Tage mit Aktivität über alle Meldungen — Punkte im Kalender
  const aktiveTage = useMemo(() => {
    const tage = new Set<string>()
    for (const b of alleBalken) {
      tage.add(tagSchluessel(b.start))
      if (!b.offen) tage.add(tagSchluessel(b.ende))
      for (const p of b.punkte) tage.add(tagSchluessel(p.zeit))
      for (const t of b.report.aktivitaetTage ?? []) tage.add(t)
    }
    return tage
  }, [alleBalken])
  const band = tag ? { links: x(tagBeginnMs(tag)), rechts: x(tagBeginnMs(tagVerschieben(tag, 1))) } : null

  // Klick auf die Achse wählt den Tag unter dem Mauszeiger
  const achseKlick = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    const t = von + ((e.clientX - r.left) / pxProStunde) * STUNDE
    if (t <= jetzt) onTag(tagSchluessel(t))
  }

  const tagLeiste = (
    <div className="flex items-center gap-1.5 text-[12.5px]">
      <span className="text-muted-foreground">Tag:</span>
      <button
        type="button"
        title="Tag davor"
        onClick={() => onTag(tagVerschieben(tag ?? heute, -1))}
        className="rounded-md px-1.5 py-0.5 font-bold text-muted-foreground hover:bg-secondary hover:text-foreground"
      >
        ‹
      </button>
      <Kalender wert={tag} onWahl={onTag} markiert={aktiveTage} />
      <button
        type="button"
        title="Tag danach"
        disabled={tag !== null && tag >= heute}
        onClick={() => onTag(tagVerschieben(tag ?? heute, 1))}
        className="rounded-md px-1.5 py-0.5 font-bold text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30"
      >
        ›
      </button>
      {tag && (
        <button
          type="button"
          onClick={() => onTag(null)}
          className="ml-1 rounded-full bg-secondary px-2.5 py-0.5 text-[12px] font-semibold text-muted-foreground hover:text-foreground"
        >
          Alle Tage
        </button>
      )}
    </div>
  )

  if (alleBalken.length === 0) {
    return (
      <div className="rounded-xl border bg-card px-6 py-12 text-center text-muted-foreground">
        Keine Meldungen für den Zeitstrahl.
      </div>
    )
  }

  return (
    <div className="rounded-xl border bg-card shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-2.5">
        {tagLeiste}
        <span className="text-[12.5px] text-muted-foreground">
          {tag
            ? `${balken.length} Meldung${balken.length === 1 ? "" : "en"} am ${_tagLang.format(tagBeginnMs(tag))} angefangen, bearbeitet oder beendet`
            : "Balken von der Meldung bis zum Abschluss — offene laufen bis jetzt."}
        </span>
        <div className="inline-flex rounded-[10px] bg-secondary p-[3px]">
          {(Object.keys(ZOOM) as Zoom[]).map((z) => (
            <button
              key={z}
              type="button"
              onClick={() => setZoom(z)}
              className={`rounded-lg px-3 py-1 text-[12px] font-semibold transition-colors ${
                zoom === z ? "bg-card text-foreground shadow-card" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {ZOOM[z].label}
            </button>
          ))}
        </div>
      </div>

      {balken.length === 0 && (
        <p className="px-6 py-10 text-center text-muted-foreground">
          An diesem Tag wurde keine Meldung angefangen, bearbeitet oder beendet.
        </p>
      )}

      <div className={`flex ${balken.length === 0 ? "hidden" : ""}`}>
        {/* Linke Spalte: steht fest, die Zeitachse scrollt */}
        <div className="shrink-0 border-r" style={{ width: LABEL_BREITE }}>
          <div className="h-9 border-b" />
          {balken.map((b) => (
            <button
              key={b.report.id}
              type="button"
              onClick={() => navigate(`/meldungen/${b.report.id}`)}
              title={b.report.titel || "ohne Titel"}
              className="flex w-full items-center gap-1.5 truncate px-3 text-left text-[12.5px] hover:bg-secondary/60"
              style={{ height: ZEILE }}
            >
              {b.report.forgejoIssueNr != null && (
                <span className="font-semibold text-primary">#{b.report.forgejoIssueNr}</span>
              )}
              <span className="truncate">{b.report.titel || "ohne Titel"}</span>
            </button>
          ))}
        </div>

        <div ref={scrollRef} className="min-w-0 flex-1 overflow-x-auto">
          <div className="relative" style={{ width: breite }}>
            {/* Gewählter Tag als Band über die ganze Höhe */}
            {band && (
              <div
                aria-hidden
                className="pointer-events-none absolute bottom-0 top-0 bg-primary/10"
                style={{ left: band.links, width: band.rechts - band.links }}
              />
            )}
            {/* Achse — Klick wählt den Tag */}
            <div
              className="relative h-9 cursor-pointer border-b"
              onClick={achseKlick}
              title="Klick: diesen Tag auswählen"
            >
              {striche.map((s) => (
                <div
                  key={s.zeit}
                  className="absolute top-0 h-full"
                  style={{ left: x(s.zeit) }}
                >
                  <span className={`absolute bottom-0 w-px ${s.haupt ? "h-3 bg-slate-400" : "h-1.5 bg-slate-500/60"}`} />
                  {s.text && (
                    <span className="absolute left-1 top-1.5 whitespace-nowrap text-[10.5px] text-muted-foreground">
                      {s.text}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Tageslinien im Hintergrund */}
            {striche
              .filter((s) => s.haupt)
              .map((s) => (
                <div
                  key={`l${s.zeit}`}
                  aria-hidden
                  className="pointer-events-none absolute bottom-0 top-9 w-px bg-border/60"
                  style={{ left: x(s.zeit) }}
                />
              ))}

            {/* Jetzt-Linie */}
            <div
              aria-hidden
              className="pointer-events-none absolute bottom-0 top-0 z-10 w-0.5 bg-primary"
              style={{ left: x(jetzt) }}
              title="jetzt"
            />

            {balken.map((b) => {
              const links = x(b.start)
              const breiteBalken = Math.max(x(b.ende) - links, 4)
              return (
                <div
                  key={b.report.id}
                  className="relative cursor-pointer hover:bg-secondary/40"
                  style={{ height: ZEILE }}
                  onClick={() => navigate(`/meldungen/${b.report.id}`)}
                >
                  <div
                    className={`absolute top-[9px] h-4 rounded border ${BALKEN[b.art]}`}
                    style={{ left: links, width: breiteBalken }}
                    title={`${_zeit.format(b.start)} – ${b.offen ? "jetzt" : _zeit.format(b.ende)}`}
                  />
                  {b.punkte.map((p, i) => (
                    <span
                      key={i}
                      className={`absolute z-[5] -translate-x-1/2 rounded-full ${PUNKT[p.art]} ${
                        tag && tagSchluessel(p.zeit) === tag
                          ? "top-[11px] h-3 w-3 ring-2 ring-foreground"
                          : "top-[13px] h-2 w-2 ring-1 ring-card"
                      }`}
                      style={{ left: x(p.zeit) }}
                      title={`${p.label} · ${_zeit.format(p.zeit)}`}
                    />
                  ))}
                  {/* Aktueller Status rechts vom Balkenende */}
                  <span
                    className="absolute top-[8px] whitespace-nowrap text-[11.5px] font-semibold text-foreground/80"
                    style={{ left: links + breiteBalken + 8 }}
                  >
                    {statusMitZug(b.report)}
                    {!b.offen && (
                      <span className="ml-1.5 font-normal text-muted-foreground">
                        {_zeit.format(b.ende)}
                      </span>
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
